import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Money } from '@/components/Money';
import { SegmentedTabs } from '@/components/SegmentedTabs';
import { Sheet } from '@/components/Sheet';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { useToast } from '@/components/Toast';
import { cn } from '@/lib/cn';
import { newIdempotencyKey } from '@/lib/ids';
import type { MenuCategory, MenuItem, ModifierGroup } from './api';
import { useCustomer } from './context';
import { Notice, PageTitle, QuantityStepper, SlowHint } from './parts';
import { useAddCartItem, useMenu } from './queries';
import { describeCustomerError } from './wording';

/**
 * The three ways to look at the menu. Every one is a filter over the server's menu: availability and the
 * special flag are the server's fields, never decided here. Hiding an item is not what stops it being
 * ordered; the server refuses an unavailable item when it is added and again when the order is placed.
 */
const VIEWS = [
  { id: 'all', label: 'All items' },
  { id: 'available', label: 'Available' },
  { id: 'specials', label: "Today's specials" },
] as const;
type View = (typeof VIEWS)[number]['id'];

function viewOf(value: string | null): View {
  return VIEWS.some((view) => view.id === value) ? (value as View) : 'all';
}

function itemsOf(categories: MenuCategory[], view: View): MenuCategory[] {
  const keep = (item: MenuItem) =>
    view === 'available' ? item.is_available : view === 'specials' ? item.is_special : true;
  return categories
    .map((category) => ({ ...category, items: category.items.filter(keep) }))
    .filter((category) => category.items.length > 0);
}

/** The white surface of a dish card: no border, a soft lift on the canvas. */
const DISH_CARD = 'rounded-surface bg-white shadow-card';

export function MenuPage() {
  const menu = useMenu({ poll: true });
  const [params, setParams] = useSearchParams();
  const view = viewOf(params.get('view'));
  const [selected, setSelected] = useState<MenuItem | null>(null);

  if (menu.isPending) {
    return (
      <>
        <PageTitle>Menu</PageTitle>
        <MenuSkeleton />
      </>
    );
  }
  if (menu.isError) {
    return (
      <>
        <PageTitle>Menu</PageTitle>
        <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />
      </>
    );
  }

  const all = itemsOf(menu.data.categories, 'all');
  if (all.length === 0) {
    return (
      <>
        <PageTitle>Menu</PageTitle>
        <EmptyState title="The menu is empty right now" message="Please ask a staff member." />
      </>
    );
  }

  const count = (id: View) =>
    itemsOf(all, id).reduce((n, category) => n + category.items.length, 0);
  const shown = itemsOf(all, view);
  const choose = (id: View) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id === 'all') next.delete('view');
        else next.set('view', id);
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <PageTitle
        aside={
          <span className="text-sm text-muted tabular-nums">
            {count('all')} {count('all') === 1 ? 'dish' : 'dishes'}
          </span>
        }
      >
        Menu
      </PageTitle>

      <SegmentedTabs
        label="Menu views"
        idPrefix="menu-tab"
        panelId="menu-panel"
        fill
        className="mb-3"
        options={VIEWS.map((option) => ({ ...option, count: count(option.id) }))}
        value={view}
        onChange={choose}
      />

      <div id="menu-panel" role="tabpanel" aria-labelledby={`menu-tab-${view}`}>
        {shown.length === 0 ? (
          view === 'specials' ? (
            <EmptyState
              title="No specials today"
              message="Have a look at the full menu instead."
              action={
                <Button variant="secondary" onClick={() => choose('all')}>
                  See all items
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="Nothing is available right now"
              message="Please ask a staff member."
            />
          )
        ) : view === 'specials' ? (
          <SpecialsList categories={shown} onOpen={setSelected} />
        ) : (
          <CategoryList categories={shown} onOpen={setSelected} />
        )}
      </div>

      {/* Keyed by item so each opening starts with fresh choices and a fresh idempotency key. */}
      {selected && (
        <ItemSheet key={selected.id} item={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function MenuSkeleton() {
  return (
    <LoadingRegion label="Loading the menu">
      <Skeleton className="mb-3 h-12" />
      <Skeleton className="mb-4 h-11 w-3/4" />
      <div className="grid gap-3 md:grid-cols-2">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className={cn(DISH_CARD, 'flex gap-3 p-3')}>
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="mt-auto h-5 w-1/3" />
            </div>
            <Skeleton className="size-28 shrink-0" />
          </div>
        ))}
      </div>
      <SlowHint />
    </LoadingRegion>
  );
}

/**
 * The dish picture. Decorative: the name is text. A dish without a picture is laid out without a picture
 * slot (see `FoodCard`), so there is no placeholder art; if a picture fails to load the slot stays a
 * quiet block of the surface colour rather than a broken-image glyph.
 */
function DishImage({ item, className }: { item: MenuItem; className?: string }) {
  const [failed, setFailed] = useState(false);
  const muted = !item.is_available && 'opacity-60 grayscale';
  if (!item.image_url || failed) {
    return <span aria-hidden="true" className={cn('block bg-surface', muted, className)} />;
  }
  return (
    <img
      src={item.image_url}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={cn('bg-surface object-cover', muted, className)}
    />
  );
}

function SpecialBadge({ onImage = false }: { onImage?: boolean }) {
  return (
    <Badge tone="attention" className={cn(onImage && 'shadow-card')}>
      <span aria-hidden="true">★</span> Today&apos;s special
    </Badge>
  );
}

/** Category chips that stay under the venue bar; the one in view, or last chosen, is marked. */
function CategoryNav({ categories }: { categories: MenuCategory[] }) {
  const [active, setActive] = useState(categories[0]?.id ?? '');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const sections = categories
      .map((category) => document.getElementById(`category-${category.id}`))
      .filter((node): node is HTMLElement => node !== null);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id.replace('category-', ''));
      },
      { rootMargin: '-120px 0px -60% 0px' },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [categories]);

  return (
    <nav
      aria-label="Menu categories"
      className="sticky top-14 z-10 -mx-4 mb-4 overflow-x-auto bg-canvas px-4 py-2 [scrollbar-width:none]"
    >
      <ul className="flex gap-2">
        {categories.map((category) => {
          const current = category.id === active;
          return (
            <li key={category.id}>
              <a
                href={`#category-${category.id}`}
                aria-current={current ? 'true' : undefined}
                onClick={() => setActive(category.id)}
                className={cn(
                  'inline-flex min-h-10 items-center rounded-full px-4 text-sm font-semibold whitespace-nowrap no-underline',
                  current
                    ? 'bg-primary text-white'
                    : 'bg-white text-ink shadow-card hover:bg-surface',
                )}
              >
                {category.name}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function CategoryList({
  categories,
  onOpen,
}: {
  categories: MenuCategory[];
  onOpen: (item: MenuItem) => void;
}) {
  return (
    <>
      {categories.length > 1 && <CategoryNav categories={categories} />}

      <div className="flex flex-col gap-7">
        {categories.map((category) => (
          <section
            key={category.id}
            id={`category-${category.id}`}
            aria-labelledby={`heading-${category.id}`}
            className="scroll-mt-28"
          >
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 id={`heading-${category.id}`} className="text-lg font-bold text-ink">
                {category.name}
              </h2>
              <span className="text-xs font-medium text-faint tabular-nums">
                {category.items.length} {category.items.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            <ul className="grid gap-3 md:grid-cols-2">
              {category.items.map((item) => (
                <li key={item.id}>
                  <FoodCard item={item} onOpen={onOpen} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

/** The "Add" pill on a dish card. Decorative: the card itself is the button. */
function AddPill({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex min-h-9 items-center gap-1 rounded-control bg-white px-4 text-sm font-bold tracking-wide text-primary shadow-dialog',
        className,
      )}
    >
      <Icon name="plus" size={14} />
      ADD
    </span>
  );
}

function SoldOutBadge() {
  return <Badge tone="neutral">Sold out</Badge>;
}

/**
 * One dish; the whole card opens the item. With a picture: words on the left, the picture with its Add
 * action on the right. Without one: the words take the full width and the Add action sits on the price
 * row. No placeholder art: a card is either a real picture or clean text.
 */
function FoodCard({ item, onOpen }: { item: MenuItem; onOpen: (item: MenuItem) => void }) {
  const { money } = useCustomer();
  const available = item.is_available;
  const pictured = item.image_url !== null;
  return (
    <button
      type="button"
      disabled={!available}
      aria-label={available ? `Add ${item.name}` : `${item.name}, sold out`}
      onClick={() => onOpen(item)}
      className={cn(
        DISH_CARD,
        'flex h-full w-full gap-3 p-3 text-left',
        available ? 'hover:bg-surface/50' : 'cursor-not-allowed',
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        {item.is_special && (
          <span>
            <SpecialBadge />
          </span>
        )}
        <span
          className={cn(
            'block text-[17px] leading-snug font-semibold',
            available ? 'text-ink' : 'text-faint',
          )}
        >
          {item.name}
        </span>
        {item.description && (
          <span className="line-clamp-2 block text-sm leading-snug text-muted">
            {item.description}
          </span>
        )}
        <span className="mt-auto flex items-center justify-between gap-2 pt-2">
          <span className="flex items-center gap-2">
            <Money
              value={money(item.base_price)}
              className={cn('text-base font-bold', available ? 'text-ink' : 'text-faint')}
            />
            {!available && <SoldOutBadge />}
          </span>
          {!pictured && available && <AddPill />}
        </span>
      </span>
      {pictured && (
        <span className="relative shrink-0 pb-4">
          <DishImage item={item} className="block size-28 rounded-control" />
          {available && <AddPill className="absolute bottom-0 left-1/2 -translate-x-1/2" />}
        </span>
      )}
    </button>
  );
}

/** Today's specials: the same items, presented large, picture first. */
function SpecialsList({
  categories,
  onOpen,
}: {
  categories: MenuCategory[];
  onOpen: (item: MenuItem) => void;
}) {
  const { money } = useCustomer();
  const items = categories.flatMap((category) =>
    category.items.map((item) => ({ item, category: category.name })),
  );
  return (
    <ul aria-label="Today's specials" className="grid gap-4 md:grid-cols-2">
      {items.map(({ item, category }) => {
        const available = item.is_available;
        const pictured = item.image_url !== null;
        return (
          <li key={item.id}>
            <button
              type="button"
              disabled={!available}
              aria-label={available ? `Add ${item.name}` : `${item.name}, sold out`}
              onClick={() => onOpen(item)}
              className={cn(
                DISH_CARD,
                'flex h-full w-full flex-col overflow-hidden text-left',
                available ? 'hover:bg-surface/50' : 'cursor-not-allowed',
              )}
            >
              {pictured && (
                <span className="relative block">
                  <DishImage item={item} className="block aspect-[4/3] w-full" />
                  <span className="absolute top-3 left-3">
                    <SpecialBadge onImage />
                  </span>
                </span>
              )}
              <span className="flex flex-1 flex-col gap-1 p-4">
                <span className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold tracking-wide text-faint uppercase">
                    {category}
                  </span>
                  {!pictured && <SpecialBadge />}
                </span>
                <span
                  className={cn(
                    'text-lg leading-snug font-bold',
                    available ? 'text-ink' : 'text-faint',
                  )}
                >
                  {item.name}
                </span>
                {item.description && (
                  <span className="text-sm leading-snug text-muted">{item.description}</span>
                )}
                <span className="mt-auto flex items-center justify-between gap-3 pt-3">
                  <Money
                    value={money(item.base_price)}
                    className={cn('text-lg font-bold', available ? 'text-ink' : 'text-faint')}
                  />
                  {available ? (
                    <span
                      aria-hidden="true"
                      className="inline-flex min-h-10 items-center gap-1 rounded-control bg-primary px-4 text-sm font-semibold text-white"
                    >
                      <Icon name="plus" size={16} />
                      Add
                    </span>
                  ) : (
                    <SoldOutBadge />
                  )}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function groupRule(group: ModifierGroup): string {
  const { min_selection: min, max_selection: max } = group;
  if (min === 0) return max === 1 ? 'Optional · choose up to 1' : `Optional · choose up to ${max}`;
  if (min === max) return `Required · choose ${min}`;
  return `Required · choose ${min} to ${max}`;
}

/** Item detail: quantity, modifier choices and a note. The server confirms every rule and sets every price. */
function ItemSheet({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  const { money, session } = useCustomer();
  const toast = useToast();
  const add = useAddCartItem();
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  // One key per "add" the customer means: a retry after a network failure cannot add the line twice.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const missing = item.modifier_groups.filter(
    (group) => (chosen[group.id]?.length ?? 0) < group.min_selection,
  );

  function toggle(group: ModifierGroup, optionId: string) {
    setChosen((current) => {
      const picked = current[group.id] ?? [];
      if (picked.includes(optionId)) {
        return { ...current, [group.id]: picked.filter((id) => id !== optionId) };
      }
      if (group.max_selection === 1) return { ...current, [group.id]: [optionId] };
      if (picked.length >= group.max_selection) return current;
      return { ...current, [group.id]: [...picked, optionId] };
    });
  }

  function submit() {
    const modifiers = Object.values(chosen)
      .flat()
      .map((id) => ({ modifier_option_id: id }));
    const trimmed = notes.trim();
    add.mutate(
      {
        input: {
          menu_item_id: item.id,
          quantity,
          modifiers,
          ...(trimmed ? { notes: trimmed } : {}),
        },
        idempotencyKey,
      },
      {
        onSuccess: () => {
          toast.show(`${item.name} added to cart`, { tone: 'success' });
          onClose();
        },
      },
    );
  }

  const failure = add.isError ? describeCustomerError(add.error, session.locationType) : null;

  return (
    <Sheet open onClose={onClose} title={item.name}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          {item.image_url && (
            <DishImage item={item} className="-mx-5 -mt-1 block aspect-[16/9] w-auto" />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Money value={money(item.base_price)} className="text-xl font-bold text-ink" />
            {item.is_special && <SpecialBadge />}
          </div>
          {item.description && (
            <p className="text-sm leading-snug text-muted">{item.description}</p>
          )}
        </div>

        {item.modifier_groups.map((group) => {
          const picked = chosen[group.id] ?? [];
          const single = group.min_selection === 1 && group.max_selection === 1;
          const full = group.max_selection > 1 && picked.length >= group.max_selection;
          return (
            <fieldset key={group.id} className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-semibold text-ink">
                {group.name} <span className="font-normal text-muted">· {groupRule(group)}</span>
              </legend>
              {group.options.map((option) => {
                const checked = picked.includes(option.id);
                const blocked = !checked && full;
                return (
                  <label
                    key={option.id}
                    className={cn(
                      'flex min-h-12 items-center justify-between gap-3 rounded-control border px-3 text-base',
                      checked
                        ? 'border-primary bg-primary-soft text-ink'
                        : 'border-line bg-white text-ink',
                      blocked && 'text-faint',
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type={single ? 'radio' : 'checkbox'}
                        name={`group-${group.id}`}
                        className="size-5 accent-primary"
                        checked={checked}
                        disabled={blocked}
                        onChange={() => toggle(group, option.id)}
                      />
                      {option.name}
                    </span>
                    {option.price_delta !== 0 && (
                      <span className="text-sm font-medium text-muted">
                        {option.price_delta > 0 ? '+' : ''}
                        <Money value={money(option.price_delta)} />
                      </span>
                    )}
                  </label>
                );
              })}
            </fieldset>
          );
        })}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="item-notes" className="text-sm font-medium text-ink">
            Note for the kitchen <span className="font-normal text-muted">(optional)</span>
          </label>
          <textarea
            id="item-notes"
            rows={2}
            maxLength={255}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="w-full rounded-control border border-field bg-white px-3.5 py-2 text-base text-ink"
          />
        </div>

        {failure && (
          <Notice tone="error">
            <strong className="block">{failure.title}</strong>
            {failure.message}
          </Notice>
        )}
        {missing.length > 0 && (
          <p id="item-missing" className="text-sm text-muted">
            Choose {missing.map((group) => group.name).join(', ')} to continue.
          </p>
        )}

        <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center gap-3 border-t border-line bg-white px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <QuantityStepper
            value={quantity}
            label={item.name}
            onChange={setQuantity}
            disabled={add.isPending}
          />
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            loading={add.isPending}
            disabled={missing.length > 0}
            aria-describedby={missing.length > 0 ? 'item-missing' : undefined}
            onClick={submit}
          >
            Add to cart
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
